import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Modal, Box, Typography, Grid, FormControl, TextField,
  Select, MenuItem, IconButton, InputLabel,
  Tooltip,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import currency from "currency.js";
import InfoIcon from '@mui/icons-material/Info';
import AutoModeIcon from "@mui/icons-material/AutoMode";
import CloseIcon from '@mui/icons-material/Close';

import {
  getContractField, getPeriodNumber, isExecutablePeriod, getPeriodDateInfo, getPeriodTaxValues
} from "../../functions";
import { ARL, CONTRACT_STATUS } from "../../constants";
import MoneyInput from "../generic/MoneyInput";

const modalStyle = {
  bgcolor: "background.paper",
  boxShadow: 24,
  left: "50%",
  maxWidth: "980px",
  p: 4,
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%"
};
const PaymentPlanModal = ({
  contract, setPaymentPlan, setArlLevel, open, onClose
}) => {
  const user = useSelector((state) => state.user.value);
  const [withNews, setWithNews] = useState(true);

  const getContractFieldWithNews = useCallback(getContractField(contract.news, contract), [contract]);

  const {
    contractBudget,
    contractPeriod,
    finishedAt,
    paymentPlan,
    paymentPlanDays
  } = useMemo(() => ({
    contractBudget: withNews ? getContractFieldWithNews('contractBudget') : contract.contractBudget,
    contractPeriod: withNews ? getContractFieldWithNews('contractPeriod') : contract.contractPeriod,
    finishedAt: withNews ? getContractFieldWithNews('finishedAt') : contract.finishedAt,
    paymentPlan: withNews ? getContractFieldWithNews('paymentPlan') : contract.paymentPlan,
    // Días ejecutables del plan que se está mostrando: con novedades, los del
    // plan vigente; sin ellas, los del plan original (calendario sin novedades
    // aplicadas). `paymentPlanDays` no es un campo almacenado, así que el plan
    // original también se deriva, con la pila de novedades vacía.
    paymentPlanDays: withNews
      ? getContractFieldWithNews('paymentPlanDays')
      : getContractField([], contract)('paymentPlanDays'),
  }), [getContractFieldWithNews, withNews, contract]);

  const balance = useMemo(
    () => contractBudget - paymentPlan?.reduce((a, b) => (a = a + b), 0),
    [contractBudget, paymentPlan]
  );

  // Rango del periodo desde la fuente única (días ejecutables del plan que se
  // muestra). Ya no se retrocede el fin cuando cae sábado o domingo: los
  // periodos se liquidan por días comerciales y no hábiles, ese ajuste no
  // existía en ninguna otra vista y era la razón por la que este modal y la
  // fila del plan de pagos mostraban fechas distintas para el mismo periodo.
  const getRangeDate = (idx) => getPeriodDateInfo(contract, paymentPlanDays, idx)?.range;

  const UpdatePaymentPlan = () => {
    const startDate = moment(contract.startedAt);
    const endDate = moment(finishedAt);

    let temp = startDate.clone();
    let monthsCount = 0;
    while (temp.isSameOrBefore(endDate, 'month')) {
      monthsCount += 1;
      temp.add(1, 'month');
    }

    const salary = contract.salary || Math.round(contractBudget / monthsCount);

    const payment = [];
    let remainingBudget = contractBudget;
    let currentMonth = startDate.clone();

    while (currentMonth.isSameOrBefore(endDate, 'month')) {
      let monthPayment = 0;
      const isFirst = currentMonth.isSame(startDate, 'month');
      const isLast = currentMonth.isSame(endDate, 'month');

      if (remainingBudget <= 0) {
        monthPayment = 0;
      } else if (isFirst && isLast) {
        const startDay = startDate.date();
        const endDay = Math.min(endDate.date(), 30);
        const workingDays = Math.max(0, endDay - startDay + 1);
        monthPayment = Math.min((workingDays / 30) * salary, remainingBudget);
      } else if (isFirst) {
        const startDay = startDate.date();
        const workingDays = Math.min(30 - startDay + 1, 30);
        monthPayment = Math.min((workingDays / 30) * salary, remainingBudget);
      } else if (isLast) {
        const endDay = Math.min(endDate.date(), 30);
        const workingDays = Math.max(1, endDay); // at least 1 day
        monthPayment = Math.min((workingDays / 30) * salary, remainingBudget);
      } else {
        monthPayment = Math.min(salary, remainingBudget);
      }

      payment.push(monthPayment);
      remainingBudget -= monthPayment;
      currentMonth.add(1, 'month');
    }

    setPaymentPlan(payment);
  };

  useEffect(() => {
    setWithNews(
      ![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(contract.status)
    );
  }, [contract.status]);

  return (
    <Modal
      open={open}
      onClose={onClose}
    >
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ flex: 1 }}>Plan de pagos a contratista</Typography>
          {![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(contract.status)
            && !!contract.news.length
            && <FormControlLabel
              label="Con novedades"
              control={(
                <Checkbox
                  checked={withNews}
                  onChange={(e) => setWithNews(e.target.checked)}
                />
              )}
            />
          }
          {(user.roles.includes('ADMIN')
            || [CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(contract.status))
            && <IconButton
              onClick={UpdatePaymentPlan}
              disabled={withNews}
            >
              <AutoModeIcon />
            </IconButton>
          }
          <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
        </Box>
        <Box sx={{ color: "gray" }}>
          Objeto del contrato: {contract.contractCode}
        </Box>
        <Grid container spacing={1} sx={{ my: 3 }}>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <DatePicker
                label="Fecha de inicio"
                defaultValue={contract.startedAt}
                readOnly
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <DatePicker
                label="Fecha de terminación"
                defaultValue={finishedAt}
                readOnly
              />
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <TextField
                required
                type="number"
                label="Duración (En días)"
                defaultValue={contractPeriod}
                InputProps={{ readOnly: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <MoneyInput
                readOnly
                label="Valor de contrato"
                value={contractBudget}
              />
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel id="arl-level">Riesgo ARL</InputLabel>
              <Select
                labelid="arl-level"
                label="Riesgo ARL"
                value={contract.arlLevel}
                onChange={(evt) => setArlLevel(evt.target.value)}
              >
                {Object.keys(ARL).map((key) => (
                  <MenuItem key={key} value={key}>{ARL[key].text}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ p: 1, backgroundColor: "#efefef", borderRadius: 1 }}>
              <Typography variant="h6" sx={{
                color: balance < 0 ? "#af635d"
                  : balance > 1 ? "#a78316" : "#5e8d5e"
              }}>
                Balance: {currency(balance, { precision: 0 }).format()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container rowSpacing={2} style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {paymentPlan?.map((pay, idx) => {
            // Con novedades aplicadas, un mes que una suspensión dejó en cero
            // no es un periodo de pago: se omite y no consume número. El plan
            // original (sin novedades) se muestra completo porque ahí un cero
            // es un valor que el administrador puede haber escrito y debe
            // poder corregir.
            if (withNews && !isExecutablePeriod(paymentPlan, idx)) return null;
            const periodNumber = withNews ? getPeriodNumber(paymentPlan, idx) : idx + 1;
            const { salarioMinimo, uvt } = getPeriodTaxValues(contract.startedAt, idx);
            const payment = (pay * 0.4) > salarioMinimo ? (pay * 0.4) : salarioMinimo;

            return (
              <React.Fragment key={idx}>
                <Grid item xs={2}>
                  <Typography sx={{ whiteSpace: "nowrap", mr: 2 }}>Mes {periodNumber}: </Typography>
                  <Typography sx={{ color: "gray", fontSize: "14px" }}>({getRangeDate(idx)})</Typography>
                </Grid>
                <Grid item xs={3}>
                  <MoneyInput
                    label="Ingrese el valor de pago correspondiente"
                    value={pay}
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(contract.status)}
                    onChange={(evt) => {
                      if ([CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(contract.status)) {
                        const plan = [...paymentPlan];
                        plan[idx] = evt.target.value;
                        setPaymentPlan(plan);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
                  <MoneyInput
                    label="Pago Seguridad Social"
                    value={payment * (0.125 + 0.16 + parseFloat(ARL[contract.arlLevel].value))}
                    disabled
                  />
                  <Tooltip
                    title={(
                      <Grid container sx={{ p: 1, fontSize: 16 }}>
                        <Grid item xs={6}>Salud</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(payment * 0.125, { precision: 0 }).format()}
                        </Grid>
                        <Grid item xs={6}>Pensión</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(payment * 0.16, { precision: 0 }).format()}
                        </Grid>
                        <Grid item xs={6}>ARL</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(payment * parseFloat(ARL[contract.arlLevel].value), { precision: 0 }).format()}
                        </Grid>
                      </Grid>
                    )}
                  >
                    <IconButton sx={{ marginLeft: -4 }}>
                      <InfoIcon sx={{ color: "gray", fontSize: 20 }}/>
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
                  <MoneyInput
                    label="Estampillas"
                    value={pay * (0.004 + (pay > 145 * uvt ? 0.01 : 0) + 0.02 + 0.006 + 0.004 + (pay > 300 * uvt ? 0.004 : 0))}
                    disabled
                  />
                  <Tooltip
                    title={(
                      <Grid container sx={{ p: 1, fontSize: 16 }}>
                        <Grid item xs={6}>D59 (IU Env)</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(pay * 0.004, { precision: 0 }).format()}
                        </Grid>
                        <Grid item xs={6}>D63 (Pro Hosp)</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {pay > 145 * uvt ? currency(pay * 0.01, { precision: 0 }).format() : 0}
                        </Grid>
                        <Grid item xs={6}>D87 (Pro Bien)</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(pay * 0.02, { precision: 0 }).format()}
                        </Grid>
                        <Grid item xs={6}>D88 (Pro Dllo)</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(pay * 0.006, { precision: 0 }).format()}
                        </Grid>
                        <Grid item xs={6}>D89 (Politécnico)</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {currency(pay * 0.004, { precision: 0 }).format()}
                        </Grid>
                        <Grid item xs={6}>D124 (IU Digital)</Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          {pay > 300 * uvt ? currency(pay * 0.004, { precision: 0 }).format() : 0}
                        </Grid>
                      </Grid>
                    )}
                  >
                    <IconButton sx={{ marginLeft: -4 }}>
                      <InfoIcon sx={{ color: "gray", fontSize: 20 }}/>
                    </IconButton>
                  </Tooltip>
                </Grid>
              </React.Fragment>
            )
          })}
        </Grid>
      </Box>
    </Modal>
  );

};

export default PaymentPlanModal;
